import { authAxiosInstance } from "$root/api/config";
import { eventBus } from "$root/event-bus";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { coordinatedRenew } from "./oidc";
import { accessToken$, impersonateEmail$ } from "../session/atom";
import { decodeTokenDates } from "../session/common";

/**
 * What a request was actually sent with, stamped by the request interceptor so its 401 can be
 * read correctly. A 401 only means "the session needs renewing" when the request carried the
 * token the app currently holds; anything older 401s for a token that has already been
 * replaced, and renewing again on its behalf is how a single expiry turns into a renew storm —
 * which the circuit breaker in `coordinatedRenew` then reads as a rejected token and answers
 * with a logout, ending a session that was never in trouble.
 */
type AuthAttempt = {
	token: string | null;
	/** Replayed because the session had already moved on to a newer token. */
	afterRotation?: boolean;
	/** Replayed with a token this request's own 401 triggered a renew for. */
	afterRenew?: boolean;
};
type AuthRequestConfig = InternalAxiosRequestConfig & { mdotmAuthAttempt?: AuthAttempt };

/**
 * Re-send a request that 401'd, picking up whatever token the interceptor now holds.
 *
 * Each request gets **one replay per cause**: the two are independent (a request can lose its
 * token to someone else's renew and then meet a genuine expiry), while a second replay for the
 * same cause would be a loop.
 */
function replay(config: AuthRequestConfig, cause: "afterRotation" | "afterRenew"): Promise<AxiosResponse> {
	config.mdotmAuthAttempt = {
		...config.mdotmAuthAttempt,
		token: config.mdotmAuthAttempt?.token ?? null,
		[cause]: true,
	};
	return authAxiosInstance(config);
}

/**
 * `true` once the token has passed its own `exp`.
 *
 * A 401 for an expired token is an ordinary expiry — recoverable — however it came about; only
 * a *live* token being refused says the session is finished. The distinction matters because a
 * replay can outlive the token it carries: `authAxiosInstance` allows `REQUEST_TIMEOUT` per
 * request, which is longer than a short Cognito access-token TTL. A token that cannot be decoded
 * counts as live, leaving the verdict to the caller.
 */
function hasExpired(token: string): boolean {
	try {
		return decodeTokenDates(token).expirationDate * 1000 <= Date.now();
	} catch {
		return false;
	}
}

/**
 * Wire the bearer token (and impersonation tag) onto every authenticated request, and turn a
 * 401 into a renew-and-replay rather than an immediate session teardown.
 *
 * `unauthorized` is emitted only once the session is beyond saving, so listeners can act on it
 * directly instead of racing each other into their own renews.
 */
export function installAuthInterceptors(): void {
	authAxiosInstance.interceptors.response.use(
		(response) => response,
		async (error) => {
			const config = error?.config as AuthRequestConfig | undefined;
			if (error?.response?.status !== 401 || !config) {
				return Promise.reject(error);
			}

			const attempt = config.mdotmAuthAttempt;
			if (!attempt?.token) {
				// Nothing to renew: the request went out without a bearer token (signed out, or
				// sent before the session was hydrated). Report it so the app can settle its
				// signed-out state; see the `unauthorized` handler in `App`.
				eventBus.emit("unauthorized");
				return Promise.reject(error);
			}

			if (readAtom(accessToken$) !== attempt.token && !attempt.afterRotation) {
				// The token was already replaced while this request was in flight, so this 401
				// says nothing about the current session. Replay it with the new token — and,
				// crucially, do not renew again on its behalf. A request overtaken a *second*
				// time falls through instead of dead-ending here: repeatedly losing the race is
				// no reason to leave the session unexamined.
				return replay(config, "afterRotation");
			}

			if (attempt.afterRenew) {
				// A token this request's own renew had just minted, refused while still live.
				// This backend keeps 401 for authentication and answers authorization failures
				// with an opaque 510, so that is the session itself being finished — not this
				// endpoint disliking this caller. Renewing again cannot fix it, and leaving the
				// app "signed in" while every request quietly fails is the worst of the options.
				// A token that expired in flight is the ordinary case instead: fail the request
				// and let the next one renew.
				if (!hasExpired(attempt.token)) {
					eventBus.emit("unauthorized");
				}
				return Promise.reject(error);
			}

			const outcome = await coordinatedRenew();
			if (outcome.status === "renewed") {
				// Renewing alone would still leave the caller with a failed request — an empty
				// widget or an error toast, which is what a recovered session looks like to the
				// user unless the request itself is retried.
				return replay(config, "afterRenew");
			}
			if (outcome.status === "postponed") {
				// The session may well be fine (see `RenewOutcome`); fail this one request
				// instead of tearing the session down.
				return Promise.reject(error);
			}
			eventBus.emit("unauthorized");
			return Promise.reject(error);
		},
	);

	authAxiosInstance.interceptors.request.use((request: AuthRequestConfig) => {
		const latestAccessToken = readAtom(accessToken$);
		if (latestAccessToken) {
			request.headers.Authorization = `Bearer ${latestAccessToken}`;
		} else {
			// Only replays arrive here already carrying a header, and a replay that outlived its
			// session (logout, or the MFA gate clearing `accessToken$`) must not go out with the
			// credential the app has just disowned — nor be mistaken below for a request that
			// never had one. No caller passes its own `Authorization` through this instance;
			// the ones that do use `publicAxiosInstance`.
			request.headers.delete("Authorization");
		}
		// Spread rather than replace: a replayed request must keep its replay markers, which are
		// what stop a permanently-rejected token from looping forever.
		request.mdotmAuthAttempt = { ...request.mdotmAuthAttempt, token: latestAccessToken };

		// While impersonating we keep the impersonator's own bearer token and
		// simply tag every request so the backend resolves it as the target user.
		const impersonateEmail = readAtom(impersonateEmail$);
		if (impersonateEmail) {
			request.headers["X-Impersonate-Email"] = impersonateEmail;
		}

		return request;
	});
}
